import { useMemo, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import Bahan from './Bahan'
import DetailKonten from './KontenDetail'
import BuatKonten from './BuatKonten'
import {
  useAntreanKonten, useTanganiGagal, useTinjauKonten, useSetujuiKonsep,
} from '../../../domains/content/api'
import { istilah } from '../../../shared/lib/istilah'
import { adaAngka, angka, tanggalJam } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, BilahTabel, Body, Chip, Gagal, Gambar, KV, KVBaris, KepalaHalaman,
  Kolom2, Kosong, Memuat, Panel, Seg, Sub, Tab, Tabel, Tombol,
} from '../../../shared/ui'
import { jelaskanGagal } from '../../../domains/content/tampil'

export { jelaskanGagal } from '../../../domains/content/tampil'

export const NADA = {
  scheduled: 'ok', published: 'ok', pending_approval: 'warn', rejected: 'idle', failed: 'bad',
  // Konsep yang menunggu persetujuan. Sama nadanya dengan `pending_approval` — keduanya
  // berarti "pekerjaannya sudah jalan dan sekarang menunggu orang".
  pending_concept: 'warn',
}

/**
 * Angka satu metrik, DIPISAH PER PLATFORM — tidak pernah dijumlahkan.
 *
 * Rancangan 7 September menyebutnya langsung: *"jangkauan Instagram dan Facebook tidak
 * bisa dijumlahkan begitu saja, karena satu orang yang melihat di keduanya terhitung dua
 * kali."* Jumlah yang salah lebih buruk daripada dua angka yang harus dibaca terpisah —
 * yang pertama dipakai orang untuk mengambil keputusan tanpa tahu ia dilebihkan.
 *
 * Versi pertama kolom ini lebih buruk lagi: ia menampilkan angka anak PERTAMA saja,
 * seolah itu keseluruhannya. Satu baris Instagram 120 dan Facebook 80 terbaca "120", dan
 * tidak ada apa pun di layar yang memberi tahu bahwa separuhnya hilang.
 *
 * Satu platform: angkanya saja, tanpa label — labelnya cuma mengulang kolom Tujuan.
 */
export function PerPlatform({ anak, medan, locale }) {
  const punya = anak.filter((a) => adaAngka(a[medan]))
  if (punya.length === 0) return <span className="text-ink3">—</span>
  if (anak.length === 1) return angka(punya[0][medan], locale)
  return (
    <span className="flex flex-col gap-0.5 leading-tight">
      {anak.map((a) => (
        <span key={a.id} className="whitespace-nowrap">
          <span className="text-[10px] text-ink3 mr-1">{singkatPlatform(a.platform)}</span>
          {adaAngka(a[medan])
            ? angka(a[medan], locale)
            : <span className="text-ink3">—</span>}
        </span>
      ))}
    </span>
  )
}

/** "IG" / "FB" — kolom angka tidak punya lebar untuk nama panjang. */
export function singkatPlatform(p) {
  const peta = { instagram: 'IG', facebook: 'FB' }
  return peta[p] || String(p || '?').slice(0, 2).toUpperCase()
}

/** Angka, atau "—" kalau platformnya tidak melaporkannya. NOL tetap ditulis nol. */
export function angkaAtauKosong(n, locale) {
  // Keputusannya di `shared/lib/waktu.js` supaya bisa diuji tanpa merender: kolom insight
  // hanya ada di tab "Sudah tayang", dan renderToString tidak bisa berpindah tab.
  if (!adaAngka(n)) return <span className="text-ink3">—</span>
  return angka(n, locale)
}



/**
 * Id SEMUA tujuan yang masih menunggu tinjauan. Satu konten ke IG dan FB adalah dua baris;
 * sampai 29 September Setujui/Tolak di baris gabungan hanya meninjau yang pertama, dan
 * platform kedua tetap menunggu tanpa tanda apa pun.
 */
export function idMenunggu(k) {
  return (k?._anak ?? (k ? [k] : [])).filter((a) => a.status === 'pending_approval')
    .map((a) => a.id)
}

/**
 * Konten buatan orang yang menunggu TANPA JAM (revisi R11): tombolnya "Jadwalkan…",
 * bukan "Setujui" — persetujuan tanpa jam ditolak rutenya (409).
 */
export function perluJadwal(k) {
  return (k?._anak ?? (k ? [k] : [])).some((a) => a.status === 'pending_approval' && !a.scheduled_at)
}

export default function Konten({ tabAwal = 'tunggu' }) {
  const { t, locale } = useBahasa()
  // Bawaannya YANG MENUNGGU ANDA. Layar dibuka untuk mengerjakan sesuatu, bukan untuk
  // melihat yang sudah terjadwal.
  //
  // `tabAwal` ADA DEMI PENGUJIAN, dan itu disebut terang-terangan: `renderToString`
  // tidak mengklik apa pun (XXIII-1), jadi tanpa cara menyetel tab dari luar, tiga dari
  // lima tab tidak akan punya satu pun penjaga.
  const [tab, setTab] = useState(tabAwal)
  const [cari, setCari] = useState('')
  // Konten yang sedang dibuka detailnya. `null` = daftar.
  const [dibuka, setDibuka] = useState(null)
  // Bahan yang dipilih di tab sebelah. Disimpan di INDUK, bukan dioper lewat URL atau
  // penyimpanan peramban: keduanya tab dari satu layar, dan keadaan yang cuma hidup
  // selama perpindahan tab tidak perlu bertahan melewati muat ulang.
  const [bahanDipakai, setBahanDipakai] = useState(null)
  // TUJUH HARI, bukan satu.
  //
  // Rutenya berbawaan satu hari — perilaku yang benar untuk tombol Telegram, dan salah
  // untuk layar ini. Pertanyaan tab "Akan tayang" adalah *apa yang akan keluar besok*,
  // dan konten yang dijadwalkan ke slot besok — yang terjadi SETIAP KALI seseorang
  // membuat konten sesudah slot terakhir hari ini lewat — tidak muncul sama sekali
  // dengan jendela satu hari. Ia tersimpan benar dan tetap terbit, tapi dari sisi orang
  // yang baru menulisnya, ia hilang.
  // TUJUH HARI KE DEPAN, TIGA PULUH KE BELAKANG.
  //
  // Jendelanya dulu hanya ke depan, dan tab "Sudah tayang" karena itu SELALU kosong:
  // konten yang sudah tayang menurut definisinya ada di masa lalu. Yang dilihat orang —
  // konten yang ia terbitkan kemarin lenyap dari seluruh layar, padahal ia tetap tayang
  // di Instagram.
  //
  // 30 hari, bukan semuanya: satu tabel tanpa batas jadi makin lambat tiap bulan tanpa
  // ada yang menyadarinya sampai layarnya berhenti memuat. Riwayat lebih jauh pekerjaan
  // penomoran halaman, dan itu belum ada.
  const q = useAntreanKonten({ hari: 7, mundur: 30 })
  const tinjau = useTinjauKonten()
  const tanganiGagal = useTanganiGagal()
  const setujuiKonsep = useSetujuiKonsep()

  const semua = Array.isArray(q.data) ? q.data : q.data?.items ?? []

  // SATU KONTEN = SATU BARIS, walau ia terbit ke dua platform.
  //
  // `automation.py` melahirkan SATU BARIS PER TUJUAN, dan itu benar di database: tiap
  // platform bisa gagal sendiri-sendiri, dan satu baris untuk dua platform tidak punya
  // tempat menyimpan "yang ini terbit, yang itu tidak".
  //
  // Tapi di LAYAR itu terbaca sebagai konten yang terduplikasi: operator memilih Instagram
  // dan Facebook, lalu melihat dua baris yang captionnya sama persis dan mengira ada yang
  // salah. Rancangan 7 September memang menaruh "Tujuan" sebagai SATU kolom.
  //
  // Digabung per `campaign_id`. Yang tidak punya campaign (konten manual lama) berdiri
  // sendiri — dikelompokkan lewat id-nya sendiri supaya tidak ada dua konten berbeda yang
  // menyatu hanya karena keduanya sama-sama kosong.
  const digabung = useMemo(() => {
    const peta = new Map()
    for (const k of semua) {
      const kunci = k.campaign_id || `sendiri:${k.id}`
      const ada = peta.get(kunci)
      if (!ada) {
        peta.set(kunci, { ...k, _anak: [k] })
        continue
      }
      ada._anak.push(k)
      // STATUS TERBURUK YANG MENANG. Satu tujuan gagal sementara yang lain terbit harus
      // terbaca sebagai "ada yang gagal" — bukan hijau yang menyembunyikan separuhnya.
      const urut = ['failed', 'pending_concept', 'pending_approval', 'draft', 'scheduled',
                    'published', 'rejected']
      if (urut.indexOf(k.status) < urut.indexOf(ada.status)) ada.status = k.status
    }
    return [...peta.values()]
  }, [semua])

  const tersaring = useMemo(() => {
    const c = cari.trim().toLowerCase()
    const sesuaiTab = digabung.filter((k) => {
      // `failed` IKUT DI "AKAN TAYANG", dan bukan sekadar demi kelengkapan.
      //
      // Sebelum ini ia tidak ada di SATU TAB PUN: konten yang gagal terbit lenyap dari
      // seluruh layar. Barisnya tetap di database lengkap dengan pesan galatnya, tapi
      // tidak ada satu pun cara melihatnya — jadi tidak ada yang tahu ia pernah ada,
      // apalagi kenapa ia gagal. Itu bentuk kegagalan paling mahal yang bisa dipunyai
      // antrean: yang hilang diam-diam tidak pernah diperbaiki siapa pun.
      //
      // Ditaruh di "Akan tayang", bukan tab sendiri: yang gagal adalah pekerjaan yang
      // BELUM selesai, dan tab tersendiri berarti satu tempat lagi yang harus diingat
      // orang untuk dibuka.
      //
      // DAFTAR YANG DIKECUALIKAN, BUKAN DAFTAR YANG DIIZINKAN — dan itu perbaikan yang
      // lebih dalam daripada menambahkan `failed` ke daftar izin kemarin.
      //
      // Bentuk lamanya mengizinkan tiga status tertentu, jadi status APA PUN di luar
      // ketiganya lenyap dari seluruh layar. `failed` cuma gejala pertamanya; `draft`
      // juga hilang, dan status baru apa pun akan hilang pada hari ia lahir — tanpa
      // galat, tanpa baris kosong, tanpa satu pun tanda. Ditemukan saat menguji nilai
      // enum yang belum diterjemahkan: barisnya tidak pernah sampai ke tabel.
      //
      // Sekarang "Akan tayang" berarti "belum keluar pintu": semua KECUALI yang sudah
      // tayang dan yang ditolak. Status baru muncul di sana secara bawaan, dan kalau ia
      // salah tempat, itu terlihat — jauh lebih murah daripada tidak terlihat.
      // TAB MENGIKUTI TAHAP SEBENARNYA (keputusan operator 16 September).
      //
      // Susunan lama menaruh `scheduled` dan `draft` bercampur di "Akan tayang",
      // sementara "Menunggu tinjauan" menyaring `pending_approval` — status yang antara
      // 15 dan 20 September TIDAK BISA LAHIR (`generate_queue` tanpa pemicu, `factory.py`
      // tercabut). Tabnya jadi museum tujuh baris dari alur yang sudah dibongkar,
      // sementara tahap yang benar-benar menunggu orang tidak punya tempat sama sekali.
      //
      // SEJAK ADR-078 IA LAHIR LAGI, dan lebih sering daripada sebelumnya: gerbang
      // `auto_approve_asset` menjatuhkan setiap baris yang gambarnya sudah jadi ke sini
      // sampai ada yang menyetujuinya — di layar ini atau lewat kartu gambar di Telegram.
      // Tombol "Setujui" di bawah memanggil rute yang sama dengan tombol Telegramnya.
      //
      // DAFTAR YANG DIKECUALIKAN DIPERTAHANKAN, dan alasannya tidak berubah: bentuk
      // "daftar yang diizinkan" membuat status apa pun di luar daftar lenyap dari
      // seluruh layar pada hari ia lahir, tanpa galat dan tanpa satu pun tanda. Itu
      // sudah terjadi dua kali — `failed` dan `draft`. `pending_concept` yang lahir
      // hari ini muncul di "Menunggu Anda" tanpa ada yang perlu menambahkannya.
      if (tab === 'tunggu') {
        return !['published', 'rejected', 'scheduled'].includes(k.status)
      }
      if (tab === 'akan') return k.status === 'scheduled'
      return k.status === 'published'
    })
    if (!c) return sesuaiTab
    return sesuaiTab.filter((k) =>
      [k.caption, k.topic, k.key_message].filter(Boolean).some((v) => String(v).toLowerCase().includes(c)),
    )
  }, [digabung, tab, cari])

  // Dihitung dari baris yang SUDAH DIGABUNG per campaign, bukan per tujuan — satu
  // konten ke dua platform adalah SATU pekerjaan yang menunggu Anda, bukan dua.
  const menunggu = digabung.filter(
    (k) => !['published', 'rejected', 'scheduled'].includes(k.status)).length
  const gagal = semua.filter((k) => k.status === 'failed')

  // DETAIL MENGGANTIKAN SELURUH LAYAR, bukan panel yang menyembul di bawah tabel.
  //
  // Isinya panjang — gambar, insight per tujuan, caption, penjelasan. Menaruhnya di bawah
  // daftar berarti orang harus menggulir melewati tabel tiap kali membuka satu konten,
  // lalu kehilangan tempatnya saat menutupnya. Rancangan 7 September juga menggantinya
  // sepenuhnya, lengkap dengan tombol "← Semua konten".
  //
  // Barisnya diambil ULANG dari `tersaring`, bukan dari state: kalau daftarnya dimuat
  // ulang (mis. sesudah menekan Buang), yang tampil di detail harus ikut berubah — bukan
  // salinan beku dari saat baris itu diklik.
  if (dibuka) {
    const kini = tersaring.find((x) => x.id === dibuka.id) || dibuka
    return (
      <DetailKonten k={kini} t={t} locale={locale} onTutup={() => setDibuka(null)} />
    )
  }

  return (
    <>
      <KepalaHalaman judul={t('konten.judul')} keterangan={t('konten.keterangan')} />

      <Tab
        aktif={tab}
        set={setTab}
        daftar={[
          { id: 'bahan', label: t('konten.bahan') },
          { id: 'tunggu', label: `${t('konten.menungguAnda')}${menunggu ? ` (${menunggu})` : ''}` },
          { id: 'akan', label: t('konten.terjadwal') },
          { id: 'terbit', label: t('konten.sudahTayang') },
          { id: 'buat', label: t('konten.buatKonten') },
        ]}
      />

      {/* Spanduk, bukan cuma baris di tabel. Konten yang gagal terbit menuntut tindakan
          orang — dan tabel yang panjang membuatnya mudah terlewat di antara yang sehat. */}
      {gagal.length > 0 && tab === 'tunggu' && (
        <Bendera nada="bad" role="alert" judul={t('konten.adaGagal', { n: gagal.length })}>
          {/* SPANDUK INI JUGA DITERJEMAHKAN. Ia sempat membuang JSON mentah Meta apa
              adanya — lengkap dengan `fbtrace_id` — ke tempat yang paling terlihat di
              layar. Ditemukan lewat kontrol negatif: asersi uji cocok DI SINI, bukan di
              baris tabelnya, jadi menerjemahkan yang di tabel saja tetap lulus sambil
              meninggalkan yang mentah di tempat yang lebih menonjol. */}
          {jelaskanGagal(gagal[0].error, t) || t('konten.gagalTanpaSebab')}
        </Bendera>
      )}

      {tinjau.isError && (
        <p className="m-0 text-[12px] text-bad">{tinjau.error?.message}</p>
      )}

      {/* Dua tab terakhir punya layarnya sendiri dan TIDAK memakai tabel antrean di
          bawah — Bahan menampilkan hasil scraping, Buat konten sebuah formulir.
          Dikembalikan lebih awal supaya tabel, bilah cari, dan penyaringnya tidak ikut
          dirender untuk tab yang tidak memakainya. */}
      {tab === 'bahan' && (
        <Bahan
          onPakai={(b) => { setBahanDipakai(b); setTab('buat') }}
        />
      )}
      {tab === 'buat' && (
        <BuatKonten bahan={bahanDipakai} bersihkanBahan={() => setBahanDipakai(null)} />
      )}

      {tab !== 'bahan' && tab !== 'buat' && (
      <Panel>
        <BilahTabel
          cari={{ nilai: cari, set: setCari }}
          ringkas={q.data ? t('umum.dari', { mulai: tersaring.length ? 1 : 0, akhir: tersaring.length, total: semua.length }) : null}
        />

        {q.isLoading && <Memuat />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {q.data && tersaring.length === 0 && (
          <Kosong
            judul={t('konten.kosong')}
            keterangan={
              tab === 'akan'
                ? 'Slot yang belum terisi untuk hari-hari ke depan itu normal — isinya dibuat scraping pada jamnya.'
                : undefined
            }
          />
        )}

        {tersaring.length > 0 && (
          <Tabel
            kepala={[
              t('konten.tayang'), t('konten.jenis'), t('masuk.isi'),
              t('konten.tujuan'),
              ...(tab === 'terbit'
                ? [t('konten.jangkauan'), t('konten.suka'), t('konten.komentar')]
                : [t('konten.keadaan')]),
              ...(tab === 'tunggu' ? [''] : []),
            ]}
          >
            {tersaring.map((k) => {
              // SATU SUMBER untuk tombol DAN untuk aksinya.
              //
              // Keduanya sempat dihitung terpisah: labelnya dari `filter(...).length`,
              // id-nya dari `filter(...).map(...)` di dalam `onClick`. Kontrol negatif
              // membuktikan itu berbahaya — mengubah id yang dikirim tidak mengubah
              // labelnya sama sekali, jadi uji pada label lulus sambil aksinya salah.
              const idGagal = (k._anak ?? [k])
                .filter((a) => a.status === 'failed').map((a) => a.id)
              return (
              <Baris key={k.id} onClick={() => setDibuka(k)}>
                <td className="font-mono text-[11.5px]">
                  {k.scheduled_at ? tanggalJam(k.scheduled_at, locale)
                    : <span className="text-ink3">{t('konten.belumDijadwalkan')}</span>}
                </td>
                <td>{k.media_type === 'video' ? 'Video' : 'Foto'}</td>
                <td className="max-w-[380px]">
                  {/* TAUTAN KE POSTINGANNYA, kalau ada. Konten yang sudah tayang dulu
                      tidak bisa dibuka sama sekali — id postingannya dibuang saat terbit.
                      Yang belum punya tautan (terbit sebelum 12 September) tetap tampil
                      sebagai teks biasa, bukan tautan mati. */}
                  {(k._anak ?? [k]).find((a) => a.tautan)?.tautan ? (
                    <a
                      href={(k._anak ?? [k]).find((a) => a.tautan).tautan}
                      target="_blank"
                      rel="noreferrer"
                      className="line-clamp-2 text-ink no-underline hover:underline"
                    >
                      {k.caption || k.topic || '—'}
                    </a>
                  ) : (
                    <span className="line-clamp-2">{k.caption || k.topic || '—'}</span>
                  )}
                  {/* SEBABNYA DALAM BAHASA ORANG, bukan JSON dari Meta.
                      `(#100) url should represent a valid URL` tidak memberi tahu siapa
                      pun apa yang harus dilakukan. Yang ditampilkan: apa artinya, dan
                      langkah berikutnya. Teks aslinya tetap ada di `title` untuk yang
                      memang mencarinya. */}
                  {(k._anak ?? [k]).filter((a) => a.status === 'failed' && a.error)
                    .slice(0, 1).map((a) => (
                      <span key={a.id} className="block mt-1 text-[11px] text-bad"
                            title={a.error}>
                        {jelaskanGagal(a.error, t)}
                      </span>
                    ))}
                </td>
                {/* SEMUA TUJUANNYA, bukan satu. Kalau keadaan tiap tujuan BERBEDA — satu
                    terbit, satu gagal — tiap chip menyebut keadaannya sendiri; kalau sama,
                    chipnya polos supaya tidak mengulang kolom di sebelahnya. */}
                <td>
                  <span className="flex flex-wrap gap-1">
                    {(k._anak ?? [k]).map((a) => {
                      const beda = (k._anak ?? []).some((x) => x.status !== k._anak[0].status)
                      return (
                        <Chip key={a.id} titik={false}
                              nada={beda ? (NADA[a.status] ?? 'idle') : 'idle'}>
                          {a.platform ?? 'instagram'}
                          {beda ? ` · ${istilah(t, 'st', a.status)}` : ''}
                        </Chip>
                      )
                    })}
                  </span>
                </td>
                {/* SATU TABEL UNTUK SEMUA PLATFORM, bukan tampilan per platform.
                    Tiga angka ini yang dipunyai setiap platform sosial. Yang KHAS —
                    simpanan di Instagram, dibagikan di Facebook — tidak jadi kolom:
                    kolom per platform berarti tiap platform baru menambah kolom yang
                    kosong untuk semua yang lain, dan tabel dua belas kolom yang sepuluh
                    di antaranya "—" tidak bisa dibaca siapa pun.

                    "—" BUKAN NOL. Platform yang tidak melaporkan sebuah angka berbeda
                    dari yang melaporkan nol; yang kedua berarti "dilihat nol orang", dan
                    menyamakannya adalah kabar buruk yang salah. */}
                {tab === 'terbit' ? (
                  <>
                    <td className="tabular"><PerPlatform anak={k._anak ?? [k]}
                                                         medan="jangkauan" locale={locale} /></td>
                    <td className="tabular"><PerPlatform anak={k._anak ?? [k]}
                                                         medan="suka" locale={locale} /></td>
                    <td className="tabular"><PerPlatform anak={k._anak ?? [k]}
                                                         medan="komentar" locale={locale} /></td>
                  </>
                ) : (
                  <td>
                    <Chip nada={NADA[k.status] ?? 'idle'}>{istilah(t, 'st', k.status)}</Chip>
                  </td>
                )}
                {tab === 'tunggu' && idGagal.length > 0 && (
                  <td className="whitespace-nowrap text-right">
                    <Tombol
                      kecil
                      jenis="sekunder"
                      disabled={tanganiGagal.isPending}
                      // SEMUA yang gagal di baris ini, bukan yang pertama saja. Satu
                      // konten ke dua platform adalah dua baris di antrean; menyentuh
                      // satu meninggalkan yang lain tetap `failed`, dan barisnya tidak
                      // hilang dari layar.
                      onClick={() => tanganiGagal.mutate({
                        ids: idGagal,
                        aksi: 'coba_lagi',
                      })}
                    >
                      {idGagal.length > 1
                        ? t('konten.cobaLagiN', { n: idGagal.length })
                        : t('konten.cobaLagi')}
                    </Tombol>{' '}
                    <Tombol
                      kecil
                      jenis="sekunder"
                      disabled={tanganiGagal.isPending}
                      onClick={() => tanganiGagal.mutate({
                        ids: idGagal,
                        aksi: 'buang',
                      })}
                    >
                      {idGagal.length > 1
                        ? t('konten.buangN', { n: idGagal.length })
                        : t('konten.buang')}
                    </Tombol>
                  </td>
                )}
                {/* SATU KOLOM, TIGA JENIS PEKERJAAN. Baris di "Menunggu Anda" bisa
                    berupa konsep yang belum disetujui, konten yang menunggu tinjauan,
                    atau draf yang tinggal diatur — dan tombolnya harus mengikuti
                    barisnya, bukan tabnya. Tombol Setujui pada sebuah draf tidak
                    menyetujui apa pun. */}
                {tab === 'tunggu' && k.status === 'pending_concept' && (
                  <td className="whitespace-nowrap text-right">
                    <Tombol
                      kecil
                      disabled={setujuiKonsep.isPending}
                      onClick={() => setujuiKonsep.mutate(k.campaign_id)}
                    >
                      {t('konten.setujuiKonsep')}
                    </Tombol>
                  </td>
                )}
                {tab === 'tunggu' && k.status === 'pending_approval' && (
                  <td className="whitespace-nowrap text-right">
                    {/* BELUM DIJADWALKAN (konten buatan orang, R11): tidak ada "Setujui"
                        tanpa jam — jamnya dipilih di detail. Yang sudah berjam disetujui
                        di sini, untuk SEMUA tujuannya sekaligus. */}
                    {perluJadwal(k) ? (
                      <Tombol kecil onClick={() => setDibuka(k)}>
                        {t('konten.jadwalkanDulu')}
                      </Tombol>
                    ) : (
                      <Tombol
                        kecil
                        disabled={tinjau.isPending}
                        onClick={() => tinjau.mutate({ ids: idMenunggu(k), keputusan: 'approve' })}
                      >
                        {t('konten.setujui')}
                      </Tombol>
                    )}{' '}
                    <Tombol
                      jenis="hantu"
                      kecil
                      disabled={tinjau.isPending}
                      onClick={() => tinjau.mutate({ ids: idMenunggu(k), keputusan: 'reject' })}
                    >
                      {t('konten.tolak')}
                    </Tombol>
                  </td>
                )}
              </Baris>
              )
            })}
          </Tabel>
        )}
      </Panel>
      )}

      {tab === 'terbit' && (
        <Panel judul={t('konten.jangkauan')}>
          <Body>
            <p className="m-0 text-[12px] text-ink2">
              Jangkauan, tayangan, suka, dan simpan <b>{t('konten.belumTersedia')}</b>: tidak ada satu tabel
              pun yang menyimpannya, dan tidak ada yang menariknya dari Meta. Yang ada cuma satu penanda
              kemampuan di adapter Instagram yang belum dipanggil siapa pun.
            </p>
          </Body>
        </Panel>
      )}
    </>
  )
}
